'use client';
import { NavigationChevronSmallDown } from '@energy-retailer/icons';
import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { forwardRef } from 'react';

import type { SelectProps } from './select.types';

const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      disabled,
      label,
      multiple,
      name,
      options,
      selectedOption,
      onChange,
      hasFixedHeight,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className="flex h-full flex-col gap-b"
      >
        <Listbox
          value={selectedOption}
          onChange={onChange}
          name={name}
          multiple={multiple}
          {...(disabled && { disabled })}
        >
          {({ open }) => (
            <>
              <Listbox.Label
                className={clsx(['text-h4', disabled && 'text-gray-200'])}
              >
                {label}
              </Listbox.Label>
              <div className="relative h-full">
                <Listbox.Button className="text-body-2 flex h-input-sm w-full items-center justify-between truncate rounded-rad-a border-none bg-white px-b py-c text-left outline-none ring-1 ring-inset ring-gray-700 transition-all hover:ring-2 hover:ring-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 active:ring-2 active:ring-blue-600 disabled:pointer-events-none disabled:text-gray-200 disabled:ring-gray-200 md:h-input-md lg:h-input-lg">
                  <span className="truncate">
                    {selectedOption ? selectedOption.name : 'Please Select'}
                  </span>
                  <NavigationChevronSmallDown
                    className={clsx([
                      'h-icon-xs w-icon-xs text-blue-500 transition-transform active:text-blue-600',
                      open ? 'rotate-180' : 'rotate-0',
                      disabled && 'text-gray-200',
                    ])}
                  />
                </Listbox.Button>
                <Transition
                  show={open}
                  enter="transition ease-in duration-100"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute z-10 mt-b w-full overflow-hidden rounded-rad-a bg-white shadow focus:outline-none">
                    <Listbox.Options
                      static
                      className={clsx({
                        'max-h-[264px] overflow-y-auto md:max-h-[237px] lg:max-h-[277px]':
                          hasFixedHeight,
                      })}
                    >
                      {options.map((option) => (
                        <Listbox.Option
                          key={option.id}
                          value={option}
                          {...(option.disabled
                            ? { disabled: option.disabled }
                            : {})}
                          className={({ active }) =>
                            `text-body-2 relative cursor-default select-none p-d md:p-b ${
                              active ? 'bg-gray-50' : 'bg-white'
                            } ${option.disabled && 'text-gray-200'}`
                          }
                        >
                          {option.name}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Transition>
              </div>
            </>
          )}
        </Listbox>
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
